import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Monitor, Smartphone } from 'lucide-react';
import { campaignsApi } from '../api/campaigns';
import { contactsApi } from '../api/contacts';
import EmailPreviewFrame from '../components/EmailPreviewFrame/EmailPreviewFrame';
import './CampaignBuilderPage.css';

// ── Config ───────────────────────────────────────────────────────────

const STEPS = [
  { id: 1, label: 'Details',   icon: '📋' },
  { id: 2, label: 'Audience',  icon: '🎯' },
  { id: 3, label: 'Content',   icon: '✉️'  },
  { id: 4, label: 'Schedule',  icon: '🚀' },
];

const CAMPAIGN_TYPES = [
  { value: 'email_outreach', label: 'Email Outreach',  desc: 'Direct outreach to contacts',     icon: '📧' },
  { value: 'newsletter',     label: 'Newsletter',      desc: 'Regular updates for subscribers', icon: '📰' },
  { value: 'announcement',   label: 'Announcement',    desc: 'Product or event news',           icon: '📢' },
  { value: 'promotional',    label: 'Promotional',     desc: 'Offers and promotions',           icon: '🎁' },
  { value: 'reengagement',   label: 'Re-engagement',   desc: 'Win back inactive contacts',      icon: '🔄' },
];

const GOALS = [
  { value: 'engagement',   label: 'Engagement',   icon: '💬', color: '#00D9FF' },
  { value: 'awareness',    label: 'Awareness',    icon: '👀', color: '#A855F7' },
  { value: 'conversions',  label: 'Conversions',  icon: '⚡', color: '#FFB800' },
  { value: 'revenue',      label: 'Revenue',      icon: '💰', color: '#39FF14' },
  { value: 'reengagement', label: 'Re-engagement',icon: '🔄', color: '#FF1493' },
];

const CONTACT_TYPES = [
  { value: 'artist',        label: 'Artists',          color: '#00D9FF' },
  { value: 'fan',           label: 'Fans',             color: '#FF1493' },
  { value: 'label',         label: 'Labels',           color: '#A855F7' },
  { value: 'agent',         label: 'Agents',           color: '#39FF14' },
  { value: 'venue',         label: 'Venues',           color: '#FF6B35' },
  { value: 'media',         label: 'Media',            color: '#00FF88' },
  { value: 'publisher',     label: 'Publishers',       color: '#FFB800' },
  { value: 'eventOrganizer',label: 'Event Organizers', color: '#FF4D4D' },
  { value: 'artistManager', label: 'Artist Managers',  color: '#C0C0C0' },
];

const STAGES = [
  { value: 'new',         label: 'New',         color: '#6B7280' },
  { value: 'contacted',   label: 'Contacted',   color: '#00D9FF' },
  { value: 'engaged',     label: 'Engaged',     color: '#A855F7' },
  { value: 'opportunity', label: 'Opportunity', color: '#FFB800' },
  { value: 'partner',     label: 'Partner',     color: '#39FF14' },
  { value: 'inactive',    label: 'Inactive',    color: '#374151' },
];

const slideVariants = {
  enter: (dir) => ({ opacity: 0, x: dir > 0 ? 40 : -40 }),
  center: { opacity: 1, x: 0, transition: { type: 'spring', stiffness: 300, damping: 28 } },
  exit:  (dir) => ({ opacity: 0, x: dir > 0 ? -40 : 40, transition: { duration: 0.15 } }),
};

// ── Step 1: Details ──────────────────────────────────────────────────

function StepDetails({ data, onChange }) {
  return (
    <div className="builder-step">
      <div className="step-header">
        <h2>Campaign Details</h2>
        <p>Give your campaign a name and a clear objective.</p>
      </div>

      <div className="builder-field">
        <label>Campaign Name <span className="required">*</span></label>
        <input
          type="text"
          className="builder-input"
          placeholder="e.g. Q2 Artist Outreach — Spring 2026"
          value={data.name}
          onChange={e => onChange('name', e.target.value)}
          autoFocus
        />
      </div>

      <div className="builder-field">
        <label>Campaign Type</label>
        <div className="type-grid">
          {CAMPAIGN_TYPES.map(t => (
            <button
              key={t.value}
              type="button"
              className={`type-card ${data.campaignType === t.value ? 'type-card--active' : ''}`}
              onClick={() => onChange('campaignType', t.value)}
            >
              <span className="type-icon">{t.icon}</span>
              <span className="type-label">{t.label}</span>
              <span className="type-desc">{t.desc}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="builder-field">
        <label>Campaign Goal</label>
        <div className="goal-pills">
          {GOALS.map(g => (
            <button
              key={g.value}
              type="button"
              className={`goal-pill ${data.goal === g.value ? 'goal-pill--active' : ''}`}
              style={data.goal === g.value ? { background: `${g.color}22`, borderColor: g.color, color: g.color } : {}}
              onClick={() => onChange('goal', data.goal === g.value ? '' : g.value)}
            >
              {g.icon} {g.label}
            </button>
          ))}
        </div>
      </div>

      <div className="builder-field">
        <label>Description <span className="optional">(optional)</span></label>
        <textarea
          className="builder-textarea"
          placeholder="What is this campaign about? What outcome are you driving toward?"
          value={data.description}
          onChange={e => onChange('description', e.target.value)}
          rows={3}
        />
      </div>
    </div>
  );
}

// ── Step 2: Audience ─────────────────────────────────────────────────

function StepAudience({ data, onChange, reachCount, loadingReach }) {
  const toggleItem = (field, value) => {
    const arr = data[field] || [];
    onChange(field, arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]);
  };

  return (
    <div className="builder-step">
      <div className="step-header">
        <h2>Target Audience</h2>
        <p>Define who receives this campaign.</p>
      </div>

      <div className="builder-field">
        <label>Who are you reaching?</label>
        <div className="target-type-tabs">
          {[
            { value: 'all',   label: 'Everyone',    icon: '👥' },
            { value: 'type',  label: 'By Type',     icon: '🏷️' },
            { value: 'stage', label: 'By Stage',    icon: '🎯' },
            { value: 'tags',  label: 'By Tags',     icon: '🔖' },
          ].map(t => (
            <button
              key={t.value}
              type="button"
              className={`target-tab ${data.targetType === t.value ? 'target-tab--active' : ''}`}
              onClick={() => onChange('targetType', t.value)}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {data.targetType === 'type' && (
        <div className="builder-field">
          <label>Select contact types</label>
          <div className="checkbox-grid">
            {CONTACT_TYPES.map(ct => (
              <label key={ct.value} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={(data.targetContactTypes || []).includes(ct.value)}
                  onChange={() => toggleItem('targetContactTypes', ct.value)}
                />
                <span className="checkbox-dot" style={{ background: ct.color }} />
                <span>{ct.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {data.targetType === 'stage' && (
        <div className="builder-field">
          <label>Select pipeline stages</label>
          <div className="stage-checkbox-grid">
            {STAGES.map(s => (
              <label key={s.value} className={`stage-checkbox ${(data.targetStages || []).includes(s.value) ? 'stage-checkbox--active' : ''}`}
                style={(data.targetStages || []).includes(s.value) ? { borderColor: s.color, background: `${s.color}15` } : {}}>
                <input
                  type="checkbox"
                  checked={(data.targetStages || []).includes(s.value)}
                  onChange={() => toggleItem('targetStages', s.value)}
                />
                <span className="stage-checkbox-dot" style={{ background: s.color }} />
                <span style={(data.targetStages || []).includes(s.value) ? { color: s.color } : {}}>{s.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {data.targetType === 'tags' && (
        <div className="builder-field">
          <label>Enter tags (comma separated)</label>
          <input
            type="text"
            className="builder-input"
            placeholder="e.g. vip, top-artist, label-partner"
            value={(data.targetTags || []).join(', ')}
            onChange={e => onChange('targetTags', e.target.value.split(',').map(t => t.trim()).filter(Boolean))}
          />
        </div>
      )}

      <div className="audience-exclude">
        <label className="toggle-row">
          <span>Exclude unsubscribed contacts</span>
          <button
            type="button"
            className={`toggle-btn ${data.excludeUnsubscribed ? 'toggle-btn--on' : ''}`}
            onClick={() => onChange('excludeUnsubscribed', !data.excludeUnsubscribed)}
          >
            <span className="toggle-knob" />
          </button>
        </label>
      </div>

      <div className="reach-estimate">
        <div className="reach-icon">🎯</div>
        <div className="reach-info">
          <span className="reach-label">Estimated Reach</span>
          {loadingReach ? (
            <span className="reach-count reach-loading">Calculating…</span>
          ) : (
            <span className="reach-count">{reachCount.toLocaleString()} contacts</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Step 3: Content ──────────────────────────────────────────────────

const TEMPLATES_API = 'https://user-emails.knkt.io/api/v1/email-templates';
const CATEGORY_COLORS = {
  promotional: '#FF1493',
  announcement: '#00D9FF',
  newsletter: '#A855F7',
  transactional: '#39FF14',
  engagement: '#FFB800',
  'thank-you': '#39FF14',
  promo: '#FF1493',
};

// Collapsible template selector
function TemplateSelector({ data, onChange, templates, loadingTemplates }) {
  const [open, setOpen] = useState(!data.templateId);

  const selected = templates.find(t => t._id === data.templateId);
  const accent = selected?.settings?.primaryColor || CATEGORY_COLORS[selected?.category] || '#FF1493';

  const pick = (id, name) => {
    onChange('templateId', id);
    onChange('templateName', name);
    onChange('contentOverrides', {});
    setOpen(false);
  };

  return (
    <div className="template-selector">
      {/* Header row */}
      <div className="ts-header" onClick={() => setOpen(o => !o)}>
        {data.templateId ? (
          <div className="ts-selected-row">
            <div className="ts-selected-accent" style={{ background: accent }} />
            <div className="ts-selected-info">
              <span className="ts-selected-name">{data.templateName}</span>
              {selected?.category && (
                <span className="ts-selected-cat" style={{ color: accent }}>{selected.category}</span>
              )}
            </div>
          </div>
        ) : (
          <span className="ts-placeholder">Choose a template…</span>
        )}
        <span className="ts-toggle-btn">
          {open ? '↑ Collapse' : data.templateId ? '↓ Change' : '↓ Browse'}
        </span>
      </div>

      {/* Expanded list */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="ts-list-wrap"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 32 }}
          >
            {loadingTemplates ? (
              <div className="template-loading" style={{ padding: '16px' }}>
                <div className="campaigns-spinner" style={{ width: 20, height: 20 }} />
                <span>Loading…</span>
              </div>
            ) : (
              <div className="ts-list">
                <button type="button"
                  className={`template-card template-card--none ${!data.templateId ? 'template-card--selected' : ''}`}
                  onClick={() => pick(null, null)}>
                  <div className="template-card-icon">✉️</div>
                  <div>
                    <div className="template-card-name">Plain Text</div>
                    <div className="template-card-desc">No template — write your own</div>
                  </div>
                  {!data.templateId && <div className="template-card-check">✓</div>}
                </button>
                {templates.map(t => {
                  const a = t.settings?.primaryColor || CATEGORY_COLORS[t.category] || '#FF1493';
                  const isSel = data.templateId === t._id;
                  return (
                    <button key={t._id} type="button"
                      className={`template-card ${isSel ? 'template-card--selected' : ''}`}
                      style={isSel ? { borderColor: a, boxShadow: `0 0 0 2px ${a}44` } : {}}
                      onClick={() => pick(t._id, t.name)}>
                      <div className="template-card-accent" style={{ background: a }} />
                      <div className="template-card-body">
                        <div className="template-card-top-row">
                          <span className="template-card-name">{t.name}</span>
                          {t.category && (
                            <span className="template-card-category"
                              style={{ background: `${a}22`, color: a, border: `1px solid ${a}44` }}>
                              {t.category}
                            </span>
                          )}
                        </div>
                        {t.description && <div className="template-card-desc">{t.description}</div>}
                      </div>
                      {isSel && <div className="template-card-check" style={{ color: a }}>✓</div>}
                    </button>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Email preview with Gmail/mobile toggle — matches user-emails EditorPage layout exactly
function TemplatePreview({ previewHtml, loading }) {
  const [mode, setMode] = useState('desktop');
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className="tpe-preview-wrap">
      <div className="tpe-header">
        <h3>Live Preview</h3>
        <div className="tpe-controls">
          <div className="tpe-mode-toggle">
            <button
              type="button"
              className={`tpe-mode-btn ${mode === 'desktop' ? 'tpe-mode-btn--active' : ''}`}
              onClick={() => setMode('desktop')}
              title="Desktop Preview"
            >
              <Monitor size={16} />
            </button>
            <button
              type="button"
              className={`tpe-mode-btn ${mode === 'mobile' ? 'tpe-mode-btn--active' : ''}`}
              onClick={() => setMode('mobile')}
              title="Mobile Preview"
            >
              <Smartphone size={16} />
            </button>
          </div>
          <button type="button" className="tpe-refresh-btn" onClick={() => setRefreshKey(k => k + 1)}>
            Refresh
          </button>
        </div>
      </div>

      <div className={`tpe-container ${mode}`}>
        {loading ? (
          <div className="tpe-preview-loading">
            <div className="campaigns-spinner" style={{ width: 32, height: 32 }} />
            <span>Rendering preview…</span>
          </div>
        ) : previewHtml ? (
          <EmailPreviewFrame key={refreshKey} html={previewHtml} mode={mode} />
        ) : (
          <div className="tpe-preview-empty">Select a template to see the preview</div>
        )}
      </div>
    </div>
  );
}

// Maps Handlebars variable paths → friendly field labels + placeholders
const VAR_FIELDS = {
  'release.title':  { label: 'Release Title',  placeholder: 'e.g. Summer Vibes EP' },
  'release.artist': { label: 'Artist Name',    placeholder: 'e.g. DJ Nova' },
  'release.date':   { label: 'Release Date',   placeholder: 'e.g. April 20, 2026' },
  'contact.name':   { label: 'Contact Name',   placeholder: 'e.g. Alex Johnson' },
  'event.name':     { label: 'Event Name',     placeholder: 'e.g. KNKT Live' },
  'event.date':     { label: 'Event Date',     placeholder: 'e.g. May 15, 2026' },
  'event.venue':    { label: 'Venue',          placeholder: 'e.g. Madison Square Garden' },
};

function extractVarPaths(blocks) {
  const allText = blocks.map(b => JSON.stringify(b.config || {})).join(' ');
  const matches = [...allText.matchAll(/\{\{([^}#/]+)\}\}/g)];
  const paths = new Set(matches.map(m => m[1].trim()));
  return [...paths].filter(p => p in VAR_FIELDS);
}

// Quick edit + test email
function QuickEditPanel({ blocks, overrides, onChange, templateId }) {
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testMsg, setTestMsg] = useState('');

  const heroBlock = blocks.find(b => b.type === 'hero');
  const ctaBlock  = blocks.find(b => b.type === 'cta');
  const detectedVars = useMemo(() => extractVarPaths(blocks), [blocks]);
  const set = (key, val) => onChange({ ...overrides, [key]: val });

  const sendTest = async () => {
    if (!testEmail) return;
    setTestSending(true);
    setTestMsg('');
    try {
      const res = await fetch(`${TEMPLATES_API}/${templateId}/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ testEmail }),
      });
      const json = await res.json();
      setTestMsg(json.success ? `✓ Sent to ${testEmail}` : `✗ ${json.message || 'Failed'}`);
    } catch (err) {
      setTestMsg(`✗ ${err.message}`);
    } finally {
      setTestSending(false);
    }
  };

  const contentBlocks = blocks.filter(b => b.type === 'content' && b.config?.body);
  const hasAnyField = heroBlock || detectedVars.length > 0 || ctaBlock?.config?.buttonText || contentBlocks.length > 0;

  return (
    <div className="qe-panel">
      <div className="qe-section-title">Customize Content</div>
      <p className="qe-hint">Override content for this campaign only. Master template unchanged.</p>

      {/* Hero block hardcoded text */}
      {heroBlock?.config?.headline && (
        <div className="builder-field">
          <label>Headline</label>
          <input type="text" className="builder-input"
            placeholder={heroBlock.config.headline}
            value={overrides.heroHeadline ?? ''}
            onChange={e => set('heroHeadline', e.target.value)} />
        </div>
      )}
      {heroBlock?.config?.subheadline && (
        <div className="builder-field">
          <label>Subheadline</label>
          <input type="text" className="builder-input"
            placeholder={heroBlock.config.subheadline}
            value={overrides.heroSubheadline ?? ''}
            onChange={e => set('heroSubheadline', e.target.value)} />
        </div>
      )}

      {/* Detected Handlebars variables */}
      {detectedVars.map(varPath => {
        const { label, placeholder } = VAR_FIELDS[varPath];
        return (
          <div key={varPath} className="builder-field">
            <label>{label}</label>
            <input type="text" className="builder-input"
              placeholder={placeholder}
              value={overrides[varPath] ?? ''}
              onChange={e => set(varPath, e.target.value)} />
          </div>
        );
      })}

      {/* Content block bodies */}
      {blocks.filter(b => b.type === 'content' && b.config?.body).map((block, idx, arr) => (
        <div key={`cbody-${idx}`} className="builder-field">
          <label>{arr.length > 1 ? `Body ${idx + 1}` : 'Body'} <span className="optional">(HTML)</span></label>
          <textarea
            className="builder-textarea"
            value={overrides[`content_body_${idx}`] ?? block.config.body}
            onChange={e => set(`content_body_${idx}`, e.target.value)}
            rows={6}
          />
        </div>
      ))}

      {/* CTA button (only if buttonText exists in config) */}
      {ctaBlock?.config?.buttonText && (
        <>
          <div className="builder-field">
            <label>Button Text</label>
            <input type="text" className="builder-input"
              placeholder={ctaBlock.config.buttonText}
              value={overrides.ctaText ?? ''}
              onChange={e => set('ctaText', e.target.value)} />
          </div>
          <div className="builder-field">
            <label>Button URL</label>
            <input type="url" className="builder-input"
              placeholder={ctaBlock.config.buttonUrl || 'https://…'}
              value={overrides.ctaUrl ?? ''}
              onChange={e => set('ctaUrl', e.target.value)} />
          </div>
        </>
      )}

      {!hasAnyField && (
        <p className="qe-no-fields">No editable fields detected in this template.</p>
      )}

      {/* Test Email */}
      <div className="qe-divider" />
      <div className="qe-section-title">Send Test Email</div>
      <div className="qe-test-row">
        <input type="email" className="builder-input"
          placeholder="test@example.com"
          value={testEmail}
          onChange={e => { setTestEmail(e.target.value); setTestMsg(''); }} />
        <button type="button" className="qe-test-btn"
          onClick={sendTest}
          disabled={testSending || !testEmail}>
          {testSending ? <span className="btn-spinner" /> : 'Send Test'}
        </button>
      </div>
      {testMsg && (
        <p className={`qe-test-msg ${testMsg.startsWith('✓') ? 'qe-test-msg--ok' : 'qe-test-msg--err'}`}>
          {testMsg}
        </p>
      )}

      <div className="qe-divider" />
      <a href="https://user-emails.knkt.io" target="_blank" rel="noreferrer" className="qe-full-editor-link">
        Open full editor in KNKT User Emails ↗
      </a>
    </div>
  );
}

function StepContent({ data, onChange }) {
  const subjectLen = (data.subject || '').length;
  const previewLen = (data.previewText || '').length;

  // Template list
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  // Blocks for the selected template
  const [blocks, setBlocks] = useState([]);
  // Base HTML fetched once per template — variables still raw e.g. {{release.title}}
  const [baseHtml, setBaseHtml] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Load template list once
  useEffect(() => {
    fetch(`${TEMPLATES_API}?limit=50&includeStarters=true`)
      .then(r => r.json())
      .then(j => setTemplates(j.data?.templates || []))
      .catch(console.error)
      .finally(() => setLoadingTemplates(false));
  }, []);

  // Fetch blocks + base HTML once when templateId changes (no re-fetch on edits)
  useEffect(() => {
    if (!data.templateId) { setBlocks([]); setBaseHtml(''); return; }
    setLoadingPreview(true);
    Promise.all([
      fetch(`${TEMPLATES_API}/${data.templateId}`).then(r => r.json()),
      fetch(`${TEMPLATES_API}/${data.templateId}/preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
      }).then(r => r.json()),
    ]).then(([tRes, pRes]) => {
      setBlocks(tRes.data?.template?.blocks || []);
      setBaseHtml(pRes.data?.html || '');
    }).catch(console.error)
      .finally(() => setLoadingPreview(false));
  }, [data.templateId]); // eslint-disable-line

  const overrides = data.contentOverrides || {};
  const hasTemplate = !!data.templateId;

  // Instant client-side replacement — no API call, no loading flicker
  const liveHtml = useMemo(() => {
    if (!baseHtml) return '';
    let html = baseHtml;
    // Replace Handlebars variables directly in the HTML
    const varMap = {
      '{{contact.name}}':   overrides['contact.name']  || '',
      '{{release.title}}':  overrides['release.title'] || '',
      '{{release.artist}}': overrides['release.artist'] || '',
      '{{release.date}}':   overrides['release.date']  || '',
      '{{event.name}}':     overrides['event.name']    || '',
      '{{event.date}}':     overrides['event.date']    || '',
      '{{event.venue}}':    overrides['event.venue']   || '',
    };
    for (const [token, val] of Object.entries(varMap)) {
      if (val) html = html.split(token).join(val);
    }
    // String-replace hardcoded hero/cta text
    const heroBlock = blocks.find(b => b.type === 'hero');
    const ctaBlock  = blocks.find(b => b.type === 'cta');
    if (overrides.heroHeadline && heroBlock?.config?.headline)
      html = html.split(heroBlock.config.headline).join(overrides.heroHeadline);
    if (overrides.heroSubheadline && heroBlock?.config?.subheadline)
      html = html.split(heroBlock.config.subheadline).join(overrides.heroSubheadline);
    if (overrides.ctaText && ctaBlock?.config?.buttonText)
      html = html.split(ctaBlock.config.buttonText).join(overrides.ctaText);
    // Replace content block bodies
    blocks.filter(b => b.type === 'content' && b.config?.body).forEach((block, idx) => {
      const overrideBody = overrides[`content_body_${idx}`];
      if (overrideBody !== undefined && block.config.body)
        html = html.split(block.config.body).join(overrideBody);
    });
    return html;
  }, [baseHtml, overrides, blocks]);

  const fields = (
    <div className="sc-fields">
      {/* Inbox preview */}
      <div className="inbox-preview">
        <div className="inbox-preview-label">Inbox Preview</div>
        <div className="inbox-row">
          <div className="inbox-avatar">K</div>
          <div className="inbox-text">
            <div className="inbox-from">KNKT Platform</div>
            <div className="inbox-subject-line">
              <span className="inbox-subject">{data.subject || 'Your subject line…'}</span>
              {' '}
              <span className="inbox-preview-text">{data.previewText || 'Preview text appears here…'}</span>
            </div>
          </div>
          <div className="inbox-time">Now</div>
        </div>
      </div>

      <div className="builder-field">
        <label>
          Subject Line <span className="required">*</span>
          <span className={`char-count ${subjectLen > 60 ? 'char-count--warn' : ''}`}>{subjectLen}/60</span>
        </label>
        <input type="text" className="builder-input"
          placeholder="e.g. Your Spring release strategy is ready 🎵"
          value={data.subject}
          onChange={e => onChange('subject', e.target.value)} />
        {subjectLen > 60 && <p className="field-hint field-hint--warn">Subject lines over 60 characters may be cut off.</p>}
      </div>

      <div className="builder-field">
        <label>
          Preview Text <span className="optional">(after subject in inbox)</span>
          <span className={`char-count ${previewLen > 90 ? 'char-count--warn' : ''}`}>{previewLen}/90</span>
        </label>
        <input type="text" className="builder-input"
          placeholder="e.g. Here's everything you need to maximize your Q2 releases…"
          value={data.previewText}
          onChange={e => onChange('previewText', e.target.value)} />
      </div>

      <div className="builder-field">
        <label>Email Template</label>
        <TemplateSelector
          data={data}
          onChange={onChange}
          templates={templates}
          loadingTemplates={loadingTemplates}
        />
      </div>

      {hasTemplate && (
        <QuickEditPanel
          blocks={blocks}
          overrides={overrides}
          onChange={val => onChange('contentOverrides', val)}
          templateId={data.templateId}
        />
      )}

      {!hasTemplate && (
        <div className="builder-field">
          <label>Campaign Notes <span className="optional">(internal only)</span></label>
          <textarea className="builder-textarea"
            placeholder="Any notes about content strategy, copy direction, or A/B test ideas…"
            value={data.notes || ''}
            onChange={e => onChange('notes', e.target.value)}
            rows={3} />
        </div>
      )}
    </div>
  );

  return (
    <div className="builder-step builder-step--content">
      <div className="step-header">
        <h2>Email Content</h2>
        <p>Craft the email your contacts will receive.</p>
      </div>

      {hasTemplate ? (
        <div className="sc-split">
          <div className="sc-split-left">{fields}</div>
          <div className="sc-split-right">
            <TemplatePreview previewHtml={liveHtml} loading={loadingPreview} />
          </div>
        </div>
      ) : fields}
    </div>
  );
}

// ── Step 4: Schedule ─────────────────────────────────────────────────

function StepSchedule({ data, onChange, onSave, saving }) {
  const cfg = CAMPAIGN_TYPES.find(t => t.value === data.campaignType);
  const goal = GOALS.find(g => g.value === data.goal);

  return (
    <div className="builder-step">
      <div className="step-header">
        <h2>Review & Schedule</h2>
        <p>Confirm your campaign details and choose when to send.</p>
      </div>

      {/* Summary */}
      <div className="review-card">
        <div className="review-row">
          <span className="review-label">Campaign</span>
          <span className="review-value">{data.name}</span>
        </div>
        {cfg && (
          <div className="review-row">
            <span className="review-label">Type</span>
            <span className="review-value">{cfg.icon} {cfg.label}</span>
          </div>
        )}
        {goal && (
          <div className="review-row">
            <span className="review-label">Goal</span>
            <span className="review-value" style={{ color: goal.color }}>{goal.icon} {goal.label}</span>
          </div>
        )}
        {data.description && (
          <div className="review-row">
            <span className="review-label">Description</span>
            <span className="review-value review-value--muted">{data.description}</span>
          </div>
        )}
        <div className="review-row">
          <span className="review-label">Audience</span>
          <span className="review-value">
            {data.targetType === 'all' ? 'Everyone' :
             data.targetType === 'type' ? `${(data.targetContactTypes || []).length} type(s)` :
             data.targetType === 'stage' ? `${(data.targetStages || []).length} stage(s)` :
             `${(data.targetTags || []).length} tag(s)`}
            {data.excludeUnsubscribed && ' · excl. unsubscribed'}
          </span>
        </div>
        {data.subject && (
          <div className="review-row">
            <span className="review-label">Subject</span>
            <span className="review-value">{data.subject}</span>
          </div>
        )}
        <div className="review-row">
          <span className="review-label">Template</span>
          <span className="review-value">{data.templateName || 'Plain Text'}</span>
        </div>
      </div>

      {/* Send options */}
      <div className="send-options">
        <div className="send-option-label">When do you want to send?</div>

        <div className="send-cards">
          <button
            type="button"
            className={`send-card ${data.status === 'draft' ? 'send-card--active' : ''}`}
            onClick={() => onChange('status', 'draft')}
          >
            <span className="send-card-icon">📝</span>
            <span className="send-card-title">Save Draft</span>
            <span className="send-card-desc">Come back and finish later</span>
          </button>

          <button
            type="button"
            className={`send-card ${data.status === 'scheduled' ? 'send-card--active' : ''}`}
            onClick={() => onChange('status', 'scheduled')}
          >
            <span className="send-card-icon">📅</span>
            <span className="send-card-title">Schedule</span>
            <span className="send-card-desc">Pick a date and time</span>
          </button>

          <button
            type="button"
            className={`send-card send-card--send ${data.status === 'send' ? 'send-card--active' : ''}`}
            onClick={() => onChange('status', 'send')}
          >
            <span className="send-card-icon">🚀</span>
            <span className="send-card-title">Send Now</span>
            <span className="send-card-desc">Launch immediately</span>
          </button>
        </div>

        {data.status === 'scheduled' && (
          <motion.div
            className="schedule-datetime"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 26 }}
          >
            <div className="datetime-fields">
              <div className="builder-field">
                <label>Date</label>
                <input
                  type="date"
                  className="builder-input"
                  value={data.scheduledDate || ''}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={e => onChange('scheduledDate', e.target.value)}
                />
              </div>
              <div className="builder-field">
                <label>Time</label>
                <input
                  type="time"
                  className="builder-input"
                  value={data.scheduledTime || '09:00'}
                  onChange={e => onChange('scheduledTime', e.target.value)}
                />
              </div>
            </div>
          </motion.div>
        )}
      </div>

      <button
        type="button"
        className={`launch-btn ${data.status === 'send' ? 'launch-btn--send' : data.status === 'scheduled' ? 'launch-btn--schedule' : ''}`}
        onClick={onSave}
        disabled={saving || !data.name}
      >
        {saving ? <span className="btn-spinner" /> : (
          data.status === 'send' ? '🚀 Launch Campaign' :
          data.status === 'scheduled' ? '📅 Schedule Campaign' :
          '📝 Save Draft'
        )}
      </button>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────

const INITIAL = {
  name: '',
  campaignType: 'email_outreach',
  goal: '',
  description: '',
  targetType: 'all',
  targetContactTypes: [],
  targetStages: [],
  targetTags: [],
  excludeUnsubscribed: true,
  subject: '',
  previewText: '',
  templateId: null,
  templateName: null,
  contentOverrides: {},
  notes: '',
  status: 'draft',
  scheduledDate: '',
  scheduledTime: '09:00',
};

export default function CampaignBuilderPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [data, setData] = useState(INITIAL);
  const [reachCount, setReachCount] = useState(0);
  const [loadingReach, setLoadingReach] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const reachTimer = useRef(null);

  const onChange = (field, value) => setData(prev => ({ ...prev, [field]: value }));

  // Live reach count when audience changes
  useEffect(() => {
    clearTimeout(reachTimer.current);
    reachTimer.current = setTimeout(async () => {
      setLoadingReach(true);
      try {
        const params = new URLSearchParams({ targetType: data.targetType, excludeUnsubscribed: data.excludeUnsubscribed });
        if (data.targetType === 'type' && data.targetContactTypes.length) params.set('contactTypes', data.targetContactTypes.join(','));
        if (data.targetType === 'stage' && data.targetStages.length) params.set('stages', data.targetStages.join(','));
        if (data.targetType === 'tags' && data.targetTags.length) params.set('tags', data.targetTags.join(','));
        const res = await contactsApi.count(params.toString());
        setReachCount(res.count || 0);
      } catch { setReachCount(0); }
      finally { setLoadingReach(false); }
    }, 400);
  }, [data.targetType, data.targetContactTypes, data.targetStages, data.targetTags, data.excludeUnsubscribed]);

  const goTo = (next) => {
    if (next < 1 || next > 4) return;
    setDirection(next > step ? 1 : -1);
    setStep(next);
  };

  const canProceed = () => {
    if (step === 1) return data.name.trim().length > 0;
    if (step === 3) return data.subject.trim().length > 0;
    return true;
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = { ...data, estimatedReach: reachCount };
      if (data.status === 'scheduled' && data.scheduledDate) {
        payload.scheduledAt = new Date(`${data.scheduledDate}T${data.scheduledTime || '09:00'}`).toISOString();
      }
      await campaignsApi.create(payload);
      navigate('/campaigns');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="campaign-builder">
      {/* Sidebar */}
      <aside className="builder-sidebar">
        <button className="builder-back" onClick={() => navigate('/campaigns')}>← Back</button>
        <div className="builder-sidebar-title">New Campaign</div>
        <nav className="builder-steps-nav">
          {STEPS.map(s => (
            <button
              key={s.id}
              className={`builder-step-btn ${step === s.id ? 'builder-step-btn--active' : ''} ${s.id < step ? 'builder-step-btn--done' : ''}`}
              onClick={() => s.id < step && goTo(s.id)}
              disabled={s.id > step}
            >
              <span className="step-num">
                {s.id < step ? '✓' : s.id}
              </span>
              <span className="step-name">{s.label}</span>
            </button>
          ))}
        </nav>

        {/* Progress */}
        <div className="builder-progress">
          <div className="builder-progress-bar">
            <div className="builder-progress-fill" style={{ height: `${((step - 1) / 3) * 100}%` }} />
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className={`builder-main ${step === 3 && data.templateId ? 'builder-main--template' : ''}`}>
        <div className={`builder-content ${step === 3 && data.templateId ? 'builder-content--wide' : ''}`}>
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={step}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              {step === 1 && <StepDetails data={data} onChange={onChange} />}
              {step === 2 && <StepAudience data={data} onChange={onChange} reachCount={reachCount} loadingReach={loadingReach} />}
              {step === 3 && <StepContent data={data} onChange={onChange} />}
              {step === 4 && <StepSchedule data={data} onChange={onChange} onSave={handleSave} saving={saving} />}
            </motion.div>
          </AnimatePresence>

          {error && <div className="builder-error">{error}</div>}

          {/* Nav buttons (not shown on step 4 which has its own CTA) */}
          {step < 4 && (
            <div className="builder-nav">
              {step > 1 && (
                <button type="button" className="nav-btn nav-btn--back" onClick={() => goTo(step - 1)}>
                  ← Back
                </button>
              )}
              <button
                type="button"
                className="nav-btn nav-btn--next"
                onClick={() => goTo(step + 1)}
                disabled={!canProceed()}
              >
                {step === 3 ? 'Review →' : 'Continue →'}
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
